import { useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';

import { useFirebase } from 'components/Firebase/index.js';
import { addFooterNotification } from 'actions/notifications.js';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { useAlert } from 'contexts/AlertContext.jsx';
import { useOrgId, useVodSettingsQuery } from 'hooks/index.js';
import useCreateVodThemeAndMakeDefaultMutation from './useCreateVodThemeAndMakeDefaultMutation.js';

const getQueryKey = (orgId) => ['vod-themes', orgId];

const sortByDate = (a, b) => {
  const aDate = a.updatedAt || a.createdAt;
  const bDate = b.updatedAt || b.createdAt;

  if (aDate > bDate) return -1;
  if (aDate < bDate) return 1;
  return 0;
};

const sortByDefault = (a, b) => {
  if (a.isDefault) return -1;
  if (b.isDefault) return 1;
  return 0;
};

export const useVodThemes = ({ shouldCreateTheme, onAbsentVodTheme } = {}, options = {}) => {
  const { data: { defaultThemeId: defaultVodThemeId } = {} , isLoading: isLoadingVodSettings } = useVodSettingsQuery();
  const { t, getErrorMessage } = useLocalization();
  const orgId = useOrgId();
  const { firestore } = useFirebase();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const addNotification = useCallback((payload) => dispatch(addFooterNotification(payload)), [dispatch]);
  const { setAlert } = useAlert();
  const history = useHistory();

  const { mutate: createThemeAndMakeItDefault, isLoading: isCreatingThemeAndMakingItDefault } =
    useCreateVodThemeAndMakeDefaultMutation({
      onError: (error) => {
        setAlert({
          title: t('themePage.errorCreatingTheme'),
          description: error.response?.text,
        });
        history.goBack();
      },
    });

  const queryKey = useMemo(() => getQueryKey(orgId), [orgId]);

  const handleSnapshotError = useCallback(
    (error) => {
      addNotification({
        title: t('themePage.errorFetchingThemes'),
        message: getErrorMessage(error.code, error.message),
        type: 'error',
      });
      console.log(error);
    },
    [addNotification, getErrorMessage, t],
  );

  const handleSnapshot = useCallback(
    async (documentSnapshot) => {
      if (isCreatingThemeAndMakingItDefault || isLoadingVodSettings) return;

      if (shouldCreateTheme && documentSnapshot.empty) {
        // Default initial case - create a new VOD theme
        // Save it as the default theme in the settings
        createThemeAndMakeItDefault();
        return;
      }

      if (!defaultVodThemeId || !documentSnapshot.docs.some((theme) => theme.id === defaultVodThemeId)) {
        // If there's no default VOD theme in the settings
        // Try to offer to re-use the default theme from the LIVE-product
        const otherDefaultTheme = documentSnapshot.docs.find(
          (theme) => theme.data().isDefault && !theme.data().isDeleted,
        );
        if (otherDefaultTheme) {
          onAbsentVodTheme?.(otherDefaultTheme.id);
        }
        return;
      }

      const docs = documentSnapshot.docs
        .map((theme) => ({ id: theme.id, ...theme.data() }))
        .filter((theme) => theme.product === 'vod')
        .sort(sortByDate)
        .sort(sortByDefault);

      const items = docs
        .filter((doc) => !doc.isDeleted)
        .map(({ id, name, isDefault, updatedAt, createdAt }) => ({
          id,
          name,
          isDefault,
          updatedAt,
          createdAt,
        }));

      const themeQueries = await Promise.all(
        items.map(({ id }) => firestore.collection('themes').doc(id).collection('style').get()),
      );

      const getTheme = (themeQuery) => {
        return themeQuery.docs.reduce((acc, documentSnapshot) => {
          acc[documentSnapshot.id] = documentSnapshot.exists ? documentSnapshot.data() : {};
          return acc;
        }, {});
      };

      const themes = themeQueries.map((themeQuery, index) => {
        const themeItem = items[index];
        return {
          ...themeItem,
          style: getTheme(themeQuery),
        };
      });

      queryClient.setQueryData(queryKey, themes);
    },
    [
      firestore,
      queryClient,
      queryKey,
      defaultVodThemeId,
      createThemeAndMakeItDefault,
      isCreatingThemeAndMakingItDefault,
      onAbsentVodTheme,
      isLoadingVodSettings,
      shouldCreateTheme,
    ],
  );

  useEffect(() => {
    if (!orgId) return;
    if (options.enabled === false) return;
    const themesQuery = firestore.collection('themes').where('orgId', '==', orgId);
    const unsubscribe = themesQuery.onSnapshot(handleSnapshot, handleSnapshotError);

    return () => {
      unsubscribe();
    };
  }, [firestore, handleSnapshot, handleSnapshotError, options.enabled, orgId]);

  return useQuery({
    queryKey,
    queryFn: () => new Promise(() => {}), // this is a dummy query, the real query is handled in the useEffect
    ...options,
  });
};

export default useVodThemes;
